import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What invoice template should I use in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If you are GST-registered, use a GST tax invoice template that includes your GSTIN, HSN/SAC code, and CGST/SGST/IGST breakdown. If you are not GST-registered (turnover below the threshold), use a standard invoice template without GST fields.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is there a free invoice template for India in PDF format?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free online invoice template for India. Fill in your details, choose GST or standard format, and download a professional PDF, up to 5 invoices per month for free without signing up.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create an invoice in Hindi using Invilic?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The invoice PDF is currently generated in English. However, you can enter Indian business names, addresses, and descriptions in the fields and they will appear on the PDF.',
      },
    },
    {
      '@type': 'Question',
      name: 'What currency should I use on Indian invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For domestic Indian clients, use INR (Indian Rupee, ₹). For international clients, you can bill in USD, GBP, EUR, or any other currency using Invilic\'s international invoice type.',
      },
    },
  ],
};

const invoiceTypes = [
  { title: 'GST Invoice Template', desc: 'For GST-registered businesses: GSTIN, HSN, CGST/SGST/IGST', to: '/create/gst', color: 'emerald' },
  { title: 'Freelancer Invoice Template', desc: 'For independent workers: no GST required', to: '/create/freelancer', color: 'blue' },
  { title: 'Service Invoice Template', desc: 'For service businesses: SAC codes, no goods fields', to: '/service-invoice-template', color: 'violet' },
  { title: 'International Invoice Template', desc: 'For Indian exporters billing foreign clients', to: '/create/international', color: 'indigo' },
];

export default function InvoiceTemplateIndiaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Template India: Free GST & Standard Invoice PDF | Invilic"
        description="Free invoice templates for India. GST invoice template with CGST/SGST/IGST, freelancer invoice template, service invoice. Download professional PDF free. No sign-up."
        canonical="https://www.invilic.com/invoice-template-india"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇮🇳 India Invoice Templates
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Template India
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice templates for Indian businesses and freelancers. GST invoice with tax breakdown, standard freelancer invoice, and international invoice for exporters. Download professional PDFs instantly.
          </p>
          <Link
            to="/create"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Choose Your Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up needed</p>
        </div>

        {/* Templates grid */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Invoice Templates for Every Indian Business
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {invoiceTypes.map(({ title, desc, to, color }) => (
              <Link
                key={title}
                to={to}
                className={`group p-6 rounded-2xl border border-${color}-100 dark:border-${color}-800/30 bg-${color}-50 dark:bg-${color}-900/10 hover:bg-${color}-100 dark:hover:bg-${color}-900/20 transition-colors`}
              >
                <div className="font-bold text-gray-900 dark:text-white mb-2 group-hover:underline">
                  {title}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{desc}</p>
                <span className={`inline-flex items-center gap-1 text-sm font-semibold text-${color}-700 dark:text-${color}-300`}>
                  Use free template <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* GST vs Non-GST guide */}
        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
            GST Invoice vs Standard Invoice: Which Do You Need?
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-400 mb-3">Use a GST Invoice if:</div>
              {[
                'Your annual turnover exceeds ₹20 lakh (services) or ₹40 lakh (goods)',
                'You are registered under GST and have a GSTIN',
                'Your client is GST-registered and needs input tax credit',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your turnover is below the GST registration threshold',
                'You are a freelancer, consultant, or small business not registered for GST',
                'Your client does not need a GST-compliant tax invoice',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Invoice Template India: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Get Your Free Invoice Template Now
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for Indian businesses. Free, no account needed.</p>
          <Link
            to="/create"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Choose Invoice Template <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

